import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import { motion } from "framer-motion";
import { RefreshCcw, Brain, ArrowLeft, Download, Share2, Loader2, Link2, FileText } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { toPng } from "html-to-image";
import jsPDF from "jspdf";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import ShareCard from "@/components/ShareCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAT_COLORS = {
  Focus: "#1C3F39",
  Resilience: "#E25F38",
  Confidence: "#345E5C",
  Motivation: "#D9A05B",
  "Anxiety Management": "#9CAE96",
};

function renderInsights(text) {
  // Render bold markdown (**...**) safely. Sanitize all generated HTML via DOMPurify.
  // Use a stable hash-like key combining line content + index to avoid index-only keys.
  const lines = text.split(/\n+/).filter(Boolean);
  return lines.map((line, i) => {
    const rawHtml = line.replace(
      /\*\*(.+?)\*\*/g,
      '<strong class="font-display text-[#1C3F39]">$1</strong>'
    );
    const safeHtml = DOMPurify.sanitize(rawHtml, {
      ALLOWED_TAGS: ["strong", "em", "br"],
      ALLOWED_ATTR: ["class"],
    });
    const key = `${i}-${line.slice(0, 24)}`;
    const cls = /^\d+\./.test(line)
      ? "text-[#111827] leading-relaxed mb-2"
      : "text-[#111827] leading-relaxed mb-3";
    return (
      <p key={key} className={cls} dangerouslySetInnerHTML={{ __html: safeHtml }} />
    );
  });
}

export default function Results() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const cardRef = useRef(null);
  const reportRef = useRef(null);

  useEffect(() => {
    const raw = sessionStorage.getItem("mindedge_result");
    const cached = raw ? JSON.parse(raw) : null;

    if (id) {
      // Public/shared link: prefer cached if id matches, otherwise fetch.
      if (cached && cached.id === id) {
        setResult(cached);
      } else {
        axios
          .get(`${API}/assessment/result/${id}`)
          .then(({ data }) => setResult(data))
          .catch(() => {
            toast.error("This result link is invalid or expired.");
            navigate("/");
          });
      }
    } else if (cached) {
      setResult(cached);
    } else {
      navigate("/");
    }
  }, [id, navigate]);

  if (!result) return null;

  const chartData = result.categories.map((c) => ({
    name: c.label,
    value: c.percentage,
  }));

  const retake = () => {
    sessionStorage.clear();
    navigate("/intake");
  };

  const renderCardPng = async () => {
    if (!cardRef.current) return null;
    // Wait one tick so the off-screen card paints (images load) before capture.
    await new Promise((r) => setTimeout(r, 100));
    return toPng(cardRef.current, {
      cacheBust: true,
      pixelRatio: 2,
      backgroundColor: "#F7F7F5",
    });
  };

  const handleDownload = async () => {
    setBusy(true);
    try {
      const dataUrl = await renderCardPng();
      if (!dataUrl) return;
      const link = document.createElement("a");
      link.download = `godspeed-${result.name.replace(/\s+/g, "-").toLowerCase()}-mental-score.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Card downloaded — share it on your socials!");
    } catch (e) {
      toast.error("Couldn't generate card. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleShare = async () => {
    setBusy(true);
    try {
      const dataUrl = await renderCardPng();
      if (!dataUrl) return;
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "godspeed-mental-score.png", { type: "image/png" });
      const shareData = {
        title: "My Mental Performance — Godspeed Sports",
        text: `I scored ${result.overall_score}% on my Godspeed Sports mental assessment. Take yours →`,
        files: [file],
      };
      if (navigator.canShare && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        toast.success("Shared!");
      } else {
        // Fallback: download
        const link = document.createElement("a");
        link.download = "godspeed-mental-score.png";
        link.href = dataUrl;
        link.click();
        toast.info("Sharing not supported here — card downloaded instead.");
      }
    } catch (e) {
      if (e?.name !== "AbortError") {
        toast.error("Couldn't share. Try downloading instead.");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleCopyLink = async () => {
    const url = `${window.location.origin}/results/${result.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Public link copied to clipboard!");
    } catch {
      window.prompt("Copy this link:", url);
    }
  };

  const handleDownloadPdf = async () => {
    if (!reportRef.current) return;
    setBusy(true);
    try {
      await new Promise((r) => setTimeout(r, 150));
      const dataUrl = await toPng(reportRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#F7F7F5",
      });
      const img = new Image();
      img.src = dataUrl;
      await new Promise((res) => (img.onload = res));

      const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 24;
      const imgW = pageW - margin * 2;
      const imgH = (img.height * imgW) / img.width;

      if (imgH <= pageH - margin * 2) {
        pdf.addImage(dataUrl, "PNG", margin, margin, imgW, imgH);
      } else {
        // Slice the tall image across multiple pages.
        const pageContentH = pageH - margin * 2;
        const sliceHpx = (pageContentH * img.width) / imgW;
        let y = 0;
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.width = img.width;
        while (y < img.height) {
          const h = Math.min(sliceHpx, img.height - y);
          canvas.height = h;
          ctx.fillStyle = "#F7F7F5";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, y, img.width, h, 0, 0, img.width, h);
          const sliceData = canvas.toDataURL("image/png");
          if (y > 0) pdf.addPage();
          pdf.addImage(sliceData, "PNG", margin, margin, imgW, (h * imgW) / img.width);
          y += h;
        }
      }
      pdf.save(`godspeed-${result.name.replace(/\s+/g, "-").toLowerCase()}-report.pdf`);
      toast.success("PDF downloaded!");
    } catch (e) {
      toast.error("Couldn't generate PDF.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F7F5] text-[#111827]">
      <header className="max-w-7xl mx-auto px-6 lg:px-12 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="brand-logo">
          <img
            src="https://customer-assets.emergentagent.com/job_sports-psych-assess/artifacts/c5xfaumz_Godspeed%20Logo%20Original%20-%20Maroon%20Final.png"
            alt="Godspeed Sports"
            className="w-[240px] h-[140px] rounded-md object-contain bg-black"
          />
        </Link>
        <Button
          onClick={retake}
          variant="outline"
          className="border-[#E5E7EB] hover:bg-[#1C3F39] hover:text-white"
          data-testid="retake-btn"
        >
          <RefreshCcw className="w-4 h-4 mr-2" strokeWidth={2.5} />
          Retake
        </Button>
      </header>

      <motion.section
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-7xl mx-auto px-6 lg:px-12 py-8"
      >
        <div ref={reportRef} className="bg-[#F7F7F5] pb-2">
        <span className="inline-block text-xs font-bold tracking-[0.22em] uppercase text-[#E25F38] mb-3">
          Your Mental Performance Profile
        </span>
        <h1 className="font-display text-4xl md:text-5xl leading-tight mb-2" data-testid="results-headline">
          {result.name}, here's your mental edge.
        </h1>
        <p className="text-[#4B5563] mb-12">
          Sport: <span className="font-semibold text-[#111827]">{result.sport}</span>
        </p>

        {/* Top row: Overall score + Pie */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          {/* Overall */}
          <div
            className="bg-white border border-[#E5E7EB] rounded-xl p-8 flex flex-col justify-between"
            data-testid="overall-card"
          >
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-[#4B5563] mb-4">
                Overall Score
              </div>
              <div className="font-display text-7xl text-[#1C3F39] leading-none">
                {result.overall_score}
                <span className="text-3xl text-[#4B5563] font-bold">%</span>
              </div>
            </div>
            <div className="mt-6">
              <div className="inline-block px-3 py-1.5 bg-[#1C3F39] text-white text-xs font-bold uppercase tracking-[0.18em] rounded-full">
                {result.overall_label}
              </div>
            </div>
          </div>

          {/* Pie chart */}
          <div
            className="bg-white border border-[#E5E7EB] rounded-xl p-6 md:col-span-2"
            data-testid="pie-card"
          >
            <div className="text-xs uppercase tracking-[0.22em] text-[#4B5563] mb-2">
              Category Breakdown
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={100}
                    paddingAngle={2}
                  >
                    {chartData.map((entry) => (
                      <Cell key={entry.name} fill={CAT_COLORS[entry.name]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => `${value}%`}
                    contentStyle={{
                      background: "#fff",
                      border: "1px solid #E5E7EB",
                      borderRadius: 8,
                      fontFamily: "Manrope",
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Legend */}
          <div
            className="bg-white border border-[#E5E7EB] rounded-xl p-6"
            data-testid="legend-card"
          >
            <div className="text-xs uppercase tracking-[0.22em] text-[#4B5563] mb-4">
              Scores
            </div>
            <div className="space-y-3">
              {result.categories.map((c) => (
                <div key={c.key} data-testid={`cat-${c.key}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span
                        className="w-2.5 h-2.5 rounded-full"
                        style={{ background: CAT_COLORS[c.label] }}
                      />
                      <span className="text-sm font-semibold">{c.label}</span>
                    </div>
                    <span className="text-sm font-bold text-[#1C3F39]">
                      {c.percentage}%
                    </span>
                  </div>
                  <div className="h-1 bg-[#F7F7F5] rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${c.percentage}%`,
                        background: CAT_COLORS[c.label],
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* AI insights */}
        <div
          className="bg-white border border-[#E5E7EB] rounded-xl p-8 md:p-12"
          data-testid="insights-card"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-[#1C3F39] flex items-center justify-center">
              <Brain className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-[#4B5563]">
                AI Mental Coach
              </div>
              <div className="font-display text-xl">Your Personalized Insights</div>
            </div>
          </div>
          <div className="prose max-w-none">{renderInsights(result.ai_insights)}</div>
        </div>

        </div>
        <div className="flex flex-wrap items-center justify-center gap-3 mt-12">
          <Button
            onClick={handleCopyLink}
            disabled={busy}
            className="bg-[#1C3F39] hover:bg-[#132A26] text-white rounded-full px-7 h-14 font-semibold"
            data-testid="copy-link-btn"
          >
            <Link2 className="w-4 h-4 mr-2" strokeWidth={2.5} />
            Copy Share Link
          </Button>
          <Button
            onClick={handleDownloadPdf}
            disabled={busy}
            className="bg-white hover:bg-[#F7F7F5] text-[#1C3F39] border-2 border-[#1C3F39] rounded-full px-7 h-14 font-semibold"
            data-testid="download-pdf-btn"
          >
            {busy ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileText className="w-4 h-4 mr-2" strokeWidth={2.5} />
            )}
            Download PDF
          </Button>
          <Button
            onClick={handleDownload}
            disabled={busy}
            variant="outline"
            className="border-[#E5E7EB] rounded-full px-7 h-14 font-semibold hover:bg-[#F7F7F5]"
            data-testid="download-card-btn"
          >
            <Download className="w-4 h-4 mr-2" strokeWidth={2.5} />
            Social Card (PNG)
          </Button>
          <Button
            onClick={handleShare}
            disabled={busy}
            className="bg-[#E25F38] hover:bg-[#c44d2a] text-white rounded-full px-7 h-14 font-semibold"
            data-testid="share-card-btn"
          >
            <Share2 className="w-4 h-4 mr-2" strokeWidth={2.5} />
            Share to Socials
          </Button>
          <Button
            onClick={retake}
            variant="ghost"
            className="rounded-full px-7 h-14 font-semibold hover:bg-[#F7F7F5]"
            data-testid="bottom-retake-btn"
          >
            <RefreshCcw className="w-4 h-4 mr-2" strokeWidth={2.5} />
            Retake
          </Button>
        </div>
      </motion.section>

      {/* Off-screen shareable card (rendered for html-to-image capture) */}
      <div
        aria-hidden="true"
        style={{
          position: "fixed",
          left: -10000,
          top: 0,
          pointerEvents: "none",
        }}
      >
        <ShareCard ref={cardRef} result={result} />
      </div>

      <footer className="border-t border-[#E5E7EB] py-8 text-center text-sm text-[#4B5563] mt-12">
        Godspeed Sports · Train the mind. Win the moment.
      </footer>
    </div>
  );
}
